import React from 'react';
import Tooltip from '@amzn/meridian/tooltip';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';

export type StackedSegment = {
  id: string;
  label: string;
  value: number;
  color?: string;
};

export type StackedCapacityBarProps = {
  total: number;
  segments: StackedSegment[];
  height?: number;
  showLegend?: boolean;
  showValues?: boolean;
  valueText?: 'percent' | 'label+percent' | 'value' | 'none';
  minLabelPct?: number;
  borderRadius?: number;
  showRemainder?: boolean;
  hideValues?: boolean;
  showOverflow?: boolean;
};

// Generates unlimited distinct colors while keeping code simple
export function generateColor(i: number): string {
  // Use golden ratio (φ ≈ 1.618) for even hue distribution
  const goldenRatio = 0.618033988749;

  // Generate hue using golden ratio for maximum visual separation
  const hue = (i * goldenRatio * 360) % 360;

  // Use consistent saturation and lightness for visual harmony
  const saturation = 75; // Vibrant but not overwhelming
  const lightness = 65; // Bright enough to be visible, dark enough for contrast

  return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
}

// A color registry for consistent color assignment
function createColorRegistry() {
  const map = new Map<string, string>();
  let next = 0;

  return {
    get(key: string): string {
      const found = map.get(key);
      if (found) return found;

      const color = generateColor(next++);
      map.set(key, color);
      return color;
    },
  };
}

// Color registry for consistent colors across all bars
const colorRegistry = createColorRegistry();

const getSegmentColor = (segment: StackedSegment, colorRegistry: ReturnType<typeof createColorRegistry>): string => {
  if (segment.color) return segment.color;
  const key = segment.id || segment.label;
  return colorRegistry.get(key);
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(n, max));
}

export const StackedCapacityBar: React.FC<StackedCapacityBarProps> = ({
  total,
  segments,
  height = 20,
  showLegend = true,
  showValues = true,
  borderRadius = 12,
  showRemainder = true,
  valueText = 'percent',
  minLabelPct = 2,
  hideValues = false,
  showOverflow = true,
}) => {
  const safeTotal = total <= 0 ? 1 : total;
  const totalUsed = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const isOverflow = totalUsed > safeTotal;
  const usedPct = isOverflow ? 1 : clamp(totalUsed / safeTotal, 0, 1);
  const target = usedPct * 100;
  const overflowPct = isOverflow ? ((totalUsed - safeTotal) / safeTotal) * 100 : 0;

  let acc = 0;
  const slices = segments.map((s, idx) => {
    const share = totalUsed > 0 ? Math.max(0, s.value) / totalUsed : 0;
    let widthPct = share * target;
    if (idx === segments.length - 1) widthPct = Math.max(0, target - acc);
    acc += widthPct;
    return {
      ...s,
      widthPct,
      displayPct: clamp((Math.max(0, s.value) / safeTotal) * 100, 0, 100),
    };
  });

  const remainderPct = Math.max(0, 100 - target);

  return (
    <Box>
      <div
        className={`stacked-capacity-bar ${isOverflow && showOverflow ? 'stacked-capacity-bar--overflow' : ''}`}
        key={`stacked-capacity-bar`}
        style={{
          height: `${height}px`,
          borderRadius: `${borderRadius}px`,
        }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(usedPct * 100)}
        aria-label={isOverflow ? 'Capacity exceeded' : 'Stacked capacity utilization'}
      >
        <Box className="stacked-capacity-bar__segments">
          {slices.map((s) => {
            const segmentColor = getSegmentColor(s, colorRegistry);
            const overflowColor = isOverflow && showOverflow ? '#ffebee' : segmentColor;
            return (
              <Tooltip
                title={
                  hideValues
                    ? `${s.label} (${s.displayPct.toFixed(1)}%)${isOverflow ? ' - OVERFLOW' : ''}`
                    : `${s.label}: ${s.value} facings (${s.displayPct.toFixed(1)}%)${isOverflow ? ' - OVERFLOW' : ''}`
                }
                id={`tooltip-${s.id}`}
                key={`tooltip-${s.id}`}
                position="top"
              >
                <div
                  key={s.id}
                  className={`stacked-capacity-bar__segment ${isOverflow && showOverflow ? 'stacked-capacity-bar__segment--overflow' : ''}`}
                  style={{
                    flex: `0 0 ${s.widthPct}%`,
                    backgroundColor: overflowColor,
                  }}
                >
                  {showValues && s.displayPct >= minLabelPct && (
                    <Column alignmentHorizontal="center" alignmentVertical="center" className="segment-label">
                      {valueText === 'percent'
                        ? `${Math.round(s.displayPct)}%`
                        : valueText === 'label+percent'
                          ? `${s.label} · ${Math.round(s.displayPct)}%`
                          : valueText === 'value'
                            ? s.value
                            : null}
                    </Column>
                  )}
                  {isOverflow && showOverflow && <Box className="stacked-capacity-bar__overflow-warning">⚠</Box>}
                </div>
              </Tooltip>
            );
          })}

          {showRemainder && remainderPct > 0 && (
            <div
              className="stacked-capacity-bar__remainder"
              style={{ flex: `0 0 ${remainderPct}%` }}
              title={`Unused: ${remainderPct.toFixed(1)}%`}
            >
              {remainderPct >= minLabelPct && (
                <Column alignmentHorizontal="center" alignmentVertical="center" className={'reminder-label '}>
                  {Math.round(remainderPct)}%
                </Column>
              )}
            </div>
          )}
        </Box>
      </div>

      {showLegend && (
        <Box className="stacked-capacity-bar__legend">
          {isOverflow && showOverflow && (
            <Box className="stacked-capacity-bar__legend-item--overflow stacked-capacity-bar__legend-overflow-text">
              ⚠ OVERFLOW: {totalUsed - safeTotal} over capacity ({overflowPct.toFixed(1)}%)
            </Box>
          )}
          {slices.map((s) => (
            <Box key={s.id} className="stacked-capacity-bar__legend-item">
              <Box
                className="stacked-capacity-bar__legend-color-box"
                backgroundColor={getSegmentColor(s, colorRegistry)}
              />
              {s.label}
            </Box>
          ))}
          {showRemainder && remainderPct > 0 && !isOverflow && (
            <Box className="stacked-capacity-bar__legend-item">
              <Box className="stacked-capacity-bar__legend-color-box--remainder" />
              Unused
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
